/**
 * @file NarrativeWidget.tsx
 *
 * @description A RJSF widget that provides an interface for editing narrative text
 * with AI-assisted features like condensing, expanding, name correction,
 * and regeneration with additional context.
 *
 * @module Widgets
 */
import { WidgetProps } from "@rjsf/utils";
import { useState } from "react";
import { Box } from "@mui/material";
import { NarrativeWidgetOptions, LoadingOperation } from "./narrative/types";
import { useNarrativeRegeneration } from "./narrative/useNarrativeRegeneration";
import { NarrativeEditor } from "./narrative/NarrativeEditor";
import { AIAssistanceTools } from "./narrative/AIAssistanceTools";
import {
  NarrativeConfirmationModal,
  OperationType,
} from "./narrative/NarrativeConfirmationModal";
import {
  getNarrativeEditorValue,
  reconcileNarrativeEditorChange,
} from "./narrative/narrativeEditorCitations";
import {
  SourceLinkedEditBar,
  SourceLinkedNarrative,
  useSourceLinkedSwap,
} from "./narrative/SourceLinkedNarrative";
import { useNarrativeCitationsEnabled } from "../chat/hooks/useNarrativeCitationsEnabled";
import { parseCitations } from "../../features/narrative-citations/parseCitations";
import type {
  Citation,
  CitationGroup,
} from "../../features/narrative-citations/types";

type NarrativeFormContext = NarrativeWidgetOptions & {
  onCite?: (group: CitationGroup) => void;
  canCite?: (citation: Citation) => boolean;
};

export const NarrativeWidget = (props: WidgetProps) => {
  const {
    id,
    value,
    label,
    onChange,
    required,
    disabled,
    readonly,
    placeholder,
    schema,
    options,
    registry,
  } = props;

  // Extract custom options from both widget options and formContext
  const widgetOptions = options as NarrativeWidgetOptions;
  const formContext = registry?.formContext as NarrativeFormContext | undefined;

  /**
   * Converts RJSF widget id to a JSON path for json_query.
   * Example: "root_Narratives_0_Text" -> "root->Narratives->0->Text"
   */
  const convertIdToJsonPath = (widgetId: string): string => {
    return widgetId.replace("root_", "root→Narrative→").replace(/_/g, "→");
  };

  /**
   * Extracts the narrative index from the RJSF widget id.
   */
  const extractNarrativeIndex = (widgetId: string): number => {
    const match = widgetId.match(/Narratives?_(\d+)/i);
    return match ? parseInt(match[1], 10) : 0;
  };

  // Convert id to JSON path for agent API
  const narrativePath = convertIdToJsonPath(id);

  const narrativeIndex =
    widgetOptions?.narrativeIndex ??
    formContext?.narrativeIndex ??
    extractNarrativeIndex(id);
  const liveWebSocket =
    widgetOptions?.liveWebSocket ?? formContext?.liveWebSocket;
  const sessionSocket =
    widgetOptions?.sessionSocket ?? formContext?.sessionSocket ?? null;
  const handleUserActivity =
    widgetOptions?.handleUserActivity ?? formContext?.handleUserActivity;
  const handleSaveChanges =
    widgetOptions?.handleSaveChanges ?? formContext?.handleSaveChanges;
  const showError = widgetOptions?.showError ?? formContext?.showError;
  const userProfile = widgetOptions?.userProfile ?? formContext?.userProfile;
  const chatSendMessage = formContext?.chatSendMessage;
  const setShowChat = formContext?.setShowChat;
  const showAIControls = formContext?.showAIControls;
  const { rawValue, displayedValue } = getNarrativeEditorValue(value);
  const { enabled: citationsEnabled } = useNarrativeCitationsEnabled();
  const hasCitedProvenance =
    Boolean(citationsEnabled && rawValue) &&
    parseCitations(rawValue).groups.length > 0;

  // State
  const [loadingOperation, setLoadingOperation] =
    useState<LoadingOperation>(null);
  // Source-linked view is the default face of a cited narrative; "Edit text"
  // swaps to the textarea. Edits keep every citation whose sentence they
  // didn't touch (reconcileNarrativeEditorChange); only when the last marker
  // drops does hasCitedProvenance go false and the textarea simply stay.
  const { proseEditing, autoFocusEdit, enterEdit, latchEdit, exitEdit } =
    useSourceLinkedSwap(readonly || false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<
    (() => void) | null
  >(null);
  const [pendingOperationType, setPendingOperationType] =
    useState<OperationType>(null);

  // Hook for AI regeneration via chat
  const { handleNarrativeRegeneration } = useNarrativeRegeneration({
    liveWebSocket,
    sessionSocket,
    narrativeIndex,
    narrativePath,
    showError,
    setLoadingOperation,
    chatSendMessage,
    setShowChat,
  });

  const minRows = (options?.rows as number) || 4;

  // Handlers

  const handleToFirstPerson = () => {
    setPendingOperationType("toFirstPerson");
    setConfirmationAction(() => async () => {
      setLoadingOperation("toFirstPerson");
      if (handleSaveChanges) await handleSaveChanges();
      await handleNarrativeRegeneration("TO_FIRST_PERSON", null);
      handleUserActivity?.(`Converted narrative to first-person perspective`);
    });
    setConfirmationModalOpen(true);
  };

  const handleToThirdPerson = (officerName: string) => {
    setPendingOperationType("toThirdPerson");
    setConfirmationAction(() => async () => {
      setLoadingOperation("toThirdPerson");
      if (handleSaveChanges) await handleSaveChanges();
      await handleNarrativeRegeneration("TO_THIRD_PERSON", { officerName });
      handleUserActivity?.(
        `Converted narrative to third-person perspective (${officerName})`,
      );
    });
    setConfirmationModalOpen(true);
  };

  const handleCorrectName = (nameToCorrect: string, correctName: string) => {
    if (!displayedValue) return;
    const updatedText = displayedValue.replace(
      new RegExp(nameToCorrect.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
      correctName,
    );
    onChange(
      reconcileNarrativeEditorChange(rawValue, displayedValue, updatedText),
    );
    handleUserActivity?.(`Corrected name in narrative`);
  };

  const handleRegenerateWithContext = (context: string) => {
    setPendingOperationType("addContext");
    setConfirmationAction(() => async () => {
      setLoadingOperation("addContext");
      if (handleSaveChanges) await handleSaveChanges();
      await handleNarrativeRegeneration("ADD_CONTEXT", {
        additionalContext: context,
      });
      handleUserActivity?.(`Regenerated narrative with additional context`);
    });
    setConfirmationModalOpen(true);
  };

  const executeConfirmation = () => {
    if (confirmationAction) {
      confirmationAction();
    }
    setConfirmationModalOpen(false);
    setPendingOperationType(null);
  };

  // The rendered, source-linked block IS the field while provenance is
  // intact: readonly always, editable until "Edit text" swaps to the
  // textarea. AI operations fall through to the editor branch so its loading
  // overlay stays the single progress affordance.
  const showSourceLinkedView =
    hasCitedProvenance && (readonly || !proseEditing) && !loadingOperation;

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      {showSourceLinkedView ? (
        <SourceLinkedNarrative
          id={id}
          label={label}
          required={required || false}
          description={schema.description}
          minRows={minRows}
          text={rawValue}
          onCite={formContext?.onCite}
          canCite={formContext?.canCite}
          {...(!readonly
            ? {
                onEdit: enterEdit,
                editDisabled: disabled || false,
                autoFocusEdit,
              }
            : {})}
        />
      ) : (
        <>
          {hasCitedProvenance && !readonly && (
            <SourceLinkedEditBar
              onDone={exitEdit}
              disabled={loadingOperation !== null}
            />
          )}
          <NarrativeEditor
            id={id}
            value={displayedValue}
            onChange={(nextValue) =>
              onChange(
                reconcileNarrativeEditorChange(
                  rawValue,
                  displayedValue,
                  nextValue,
                ),
              )
            }
            disabled={disabled || readonly || false}
            readonly={readonly || false}
            loadingOperation={loadingOperation}
            placeholder={placeholder}
            label={label}
            required={required || false}
            minRows={minRows}
            maxRows={12}
            description={schema.description}
            autoFocus={hasCitedProvenance && proseEditing}
            onFocus={latchEdit}
          />
        </>
      )}
      {showAIControls && (
        <AIAssistanceTools
          loadingOperation={loadingOperation}
          disabled={disabled || readonly || false}
          onToFirstPerson={handleToFirstPerson}
          onToThirdPerson={handleToThirdPerson}
          onCorrectName={handleCorrectName}
          onRegenerateWithContext={handleRegenerateWithContext}
          userProfile={userProfile}
        />
      )}

      <NarrativeConfirmationModal
        open={confirmationModalOpen}
        onClose={() => {
          setConfirmationModalOpen(false);
          setPendingOperationType(null);
        }}
        onConfirm={executeConfirmation}
        operationType={pendingOperationType}
      />
    </Box>
  );
};
